import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ThumbsDown, ThumbsUp } from "lucide-react";

import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import ViewToggle from "./components/ViewToggle";
import AIInputBox from "./components/AIInputBox";
import ChartContainer from "./components/ChartContainer";
import TableView from "./components/TableView";
import IconViewer from "./components/iconviewer/iconviewer";
import NoteSection from "./components/NoteSection";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8009";

const RenderTitle = ({ title, theme = "light" }) => {
  if (!title) return null;
  return (
    <span
      className={`text-lg font-semibold line-clamp-2 ${
        theme === "dark" ? "text-slate-200" : "text-slate-800"
      }`}
    >
      {title}
    </span>
  );
};

function App() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [icon, setIcon] = useState("default");
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState("light");
  const [history, setHistory] = useState([]);
  const [rating, setRating] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [listening, setListening] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [voiceLang, setVoiceLang] = useState("en-US");

  const [userEmail] = useState(() => {
    let email = localStorage.getItem("user_email");
    if (!email) {
      email = `user_${uuidv4()}@app.com`;
      localStorage.setItem("user_email", email);
    }
    return email;
  });

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/history/${userEmail}`);
      const data = await res.json();
      setHistory(data.rows || data.data || data || []);
    } catch (err) {
      console.error("History error:", err);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const recognitionRef = useRef(null);

  const startVoiceInput = (langOverride) => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech Recognition not supported in this browser");
      return;
    }

    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.lang = langOverride || voiceLang;
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;

    recognitionRef.current.start();
    setListening(true);

    recognitionRef.current.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuery((prev) => `${prev} ${transcript}`.trim());
    };

    recognitionRef.current.onend = () => {
      setListening(false);
    };

    recognitionRef.current.onerror = () => {
      setListening(false);
    };
  };

  const openLanguageMenu = (e) => {
    e.preventDefault();
    setShowLangMenu((prev) => !prev);
  };

  const selectLanguage = (lang) => {
    setVoiceLang(lang.code);
    setShowLangMenu(false);
    setTimeout(() => startVoiceInput(lang.code), 0);
  };

  const languageOptions = useMemo(
    () => [
      { name: "English", code: "en-US" },
      { name: "French", code: "fr-FR" },
      { name: "German", code: "de-DE" },
    ],
    [],
  );

  const clearQuery = () => {
    setQuery("");
  };

  const quickPrompts = useMemo(
    () => [
      "Show top 20 customers by total revenue for the last 12 months",
      "List customers with no orders in the last 6 months",
      "Show total revenue and number of orders per customer for 2025",
      "Show revenue by country for the last 12 months",
      "List top customers in France by revenue",
      "Show customers grouped by sales person with their total revenue",
      "Show customers with strong revenue growth over the last 6 months",
      "List customers with high revenue but low number of orders",
      "Show customers with frequent orders but low average order value",
      "Show customers with no activity",
      "Show my best customers",
      "Show inactive customers with high past revenue",
    ],
    [],
  );

  const handleQuickPrompt = () => {
    const randomPrompt =
      quickPrompts[Math.floor(Math.random() * quickPrompts.length)];
    setQuery(randomPrompt);
  };

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!query.trim()) return;

    setIcon("thinking");
    setLoading(true);
    setError(null);
    setResponse(null);
    setRating(null);

    try {
      const res = await fetch(`${API_BASE}/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, user_email: userEmail }),
      });

      const data = await res.json();
      console.log(data,"data");
      if (!res.ok) throw new Error(JSON.stringify(data));
      setResponse(data);
      setIcon("success");
      fetchHistory();
    } catch (err) {
      setIcon("error");
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const rate = async (value) => {
    if (!response?.log_id) return;

    await fetch(`${API_BASE}/rate?log_id=${response.log_id}&rating=${value}`, {
      method: "POST",
    });

    setRating(value);
    fetchHistory();
  };

  const openHistory = async (logId) => {
    try {
      setIcon("thinking");
      setLoading(true);
      setError(null);
      setResponse(null);
      setRating(null);

      const res = await fetch(`${API_BASE}/history-detail/${logId}`);
      const data = await res.json();

      if (data.status === "success") {
        setQuery((prev) => data.data?.user_prompt || prev);
        console.log(data,"d2d")
        setResponse({
          log_id: logId,
          title:data.title,
      
          note:data?.note,

          data: {
            rows: data.data.rows,
            columns: data.data.columns,
          },
        });
        setRating(data.data.response_quality || null);
        setIcon("success");
      }
      else {
      setError(data.message || "Failed to load history");
      setIcon("error");
    }
    } catch (error) {
      console.error("History load failed:", error);
    } finally{
      setLoading(false);
    }
  };

  const handleSelectHistory = (item) => {
    if (item?.user_prompt) {
      setQuery(item.user_prompt);
    }
    if (item?.id) {
      openHistory(item.id);
    }
  };

  const parsedTitle = useMemo(() => {
    if (!response?.title) return {};
    try {
      return JSON.parse(response.title);
    } catch {
      return { Title: response.title };
    }
  }, [response?.title]);

  return (
    <div
      className={`h-screen w-full overflow-hidden ${
        theme === "dark" ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-800"
      }`}
    >
      <Sidebar
        history={history}
        activeId={response?.log_id}
        onSelectHistory={handleSelectHistory}
        theme={theme}
        isOpen={isSidebarOpen}
      />

      <div className={`${isSidebarOpen ? "ml-64" : "ml-0"} flex h-screen flex-col transition-all duration-300`}>
        <Header
          theme={theme}
          onToggleTheme={toggleTheme}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)}
        />

        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm ${
                theme === "dark" ? "bg-slate-900" : "bg-white"
              }`}
            >
              <IconViewer img={icon} />
            </div>
            <div className="flex-1">
              <AIInputBox
                query={query}
                setQuery={setQuery}
                onSubmit={submit}
                onClear={clearQuery}
                loading={loading}
                listening={listening}
                theme={theme}
                onQuickPrompt={handleQuickPrompt}
                onToggleMic={() => {
                  if (listening) {
                    recognitionRef.current?.stop();
                    setListening(false);
                    return;
                  }
                  if (showLangMenu) {
                    setShowLangMenu(false);
                    startVoiceInput();
                    return;
                  }
                  setShowLangMenu(true);
                }}
                onOpenLanguageMenu={openLanguageMenu}
                showLangMenu={showLangMenu}
                languages={languageOptions}
                onSelectLanguage={selectLanguage}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
              {error}
            </div>
          )}

          {response?.data?.error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
              Something went wrong. Please try again later.
            </div>
          )}

          <div
            className={`flex h-full min-h-0 flex-col rounded-2xl border p-3 shadow-sm ${
              theme === "dark" ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <ViewToggle value={viewMode} onChange={setViewMode} />
              </div>
                <RenderTitle title={parsedTitle?.Title || ""} theme={theme} />
            </div>

            {response?.message && (
              <div
                className={`mt-3 max-h-28 overflow-auto rounded-2xl border p-4 text-sm shadow-sm ${
                  theme === "dark"
                    ? "border-slate-800 bg-slate-900 text-slate-200"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
              >
                {response.message}
              </div>
            )}

            <div className="mt-3 flex-1 min-h-0 overflow-hidden">
              <div className="relative h-full">
                <div
                  className={`absolute inset-0 transition-all duration-300 ${
                    viewMode === "chart" ? "opacity-100" : "opacity-0 pointer-events-none"
                  }`}
                >
                  <ChartContainer
                    key={`chart-${viewMode}`}
                    response={response}
                    data={response?.data}
                    title={parsedTitle}
                    loading={loading}
                    theme={theme}
                  />
                </div>
                <div
                  className={`absolute inset-0 transition-all duration-300 ${
                    viewMode === "table" ? "opacity-100" : "opacity-0 pointer-events-none"
                  }`}
                >
                  <TableView response={response} loading={loading} theme={theme} />
                </div>
              </div>
            </div>
          </div>

          {response && (
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <button
                type="button"
                onClick={() => rate(9)}
                aria-label="Helpful"
                title="Helpful"
                className={`rounded-xl p-2 transition ${
                  rating === 9
                    ? "bg-[#cc0452] text-white"
                    : "border border-[#cc0452] bg-white text-[#cc0452] hover:bg-[#cc0452] hover:text-white"
                }`}
              >
                <ThumbsUp size={16} />
              </button>
              <button
                type="button"
                onClick={() => rate(1)}
                aria-label="Needs work"
                title="Needs work"
                className={`rounded-xl p-2 transition ${
                  rating === 1
                    ? "bg-[#cc0452] text-white"
                    : "border border-[#cc0452] bg-white text-[#cc0452] hover:bg-[#cc0452] hover:text-white"
                }`}
              >
                <ThumbsDown size={16} />
              </button>
              
             {
              response?.note && (
                <NoteSection note={response.note}/>
              )
             } 
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
